import { LockClosedIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

import EntrepreneurTermsNConditions from "./EntrepreneurTermsNConditions";
import Footer from "./Footer";
import TopBar from "./Topbar";

/**
 * AQI – Hero + Call‑to‑Action section
 * -------------------------------------------------------------
 * Re‑creates the layout you shared:
 *   • Blurred hero image with overlay, headline, and sub‑headline
 *   • Mission copy on a white band underneath
 *   • Three primary CTA buttons
 *
 * TailwindCSS is used for styling. Adjust image paths, text, and routes
 * as needed to plug into your project.
 */
const EntrepreneurLandingPage = () => {
    const [showModal, setShowModal] = useState(false);
    const [modalStep, setModalStep] = useState(1);

    const navigate = useNavigate();

    const isUserLoggedIn = () => {
        try {
            const user = JSON.parse(localStorage.getItem("user") || "null");
            if (user && (user.id || user.userId || user.email)) return true;
        } catch (e) {
            // ignore parse errors
        }

        // Fallback to explicit flag only — avoid treating stray tokens/ids as active session
        return localStorage.getItem("userLoggedIn") === "true";
    };
    
    const handleEntrepreneurClick = () => {
        // Show popup instead of navigating directly
        setShowModal(true);
    };

    const handleAnswer = (isWomanOwned) => {
        if(isWomanOwned) {
            // Show second modal
            setModalStep(2);
        } else {
            // Close modal if not eligible
            setModalStep(3);
        }
    };

    const handleGoHome = () => {
        setShowModal(false);

        // Or your home route
        navigate("/");
    };

    const handleChecklistYes = () => {
        // Show T&C modal
        setModalStep(5);
    };

    const handleFinalContinue = () => {
        setModalStep(4);
    };

    const bigModal =
        "w-full max-w-sm sm:max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-5xl max-h-[85vh] overflow-y-auto";
    const smallModal = "w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl";

    return (
        <div className="font-body">
            <TopBar />
            <section className="font-body">
                <div className="relative overflow-hidden bg-navy text-paper">
                    <div
                        className="absolute inset-0 opacity-[0.07]"
                        style={{
                            backgroundImage:
                                "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
                            backgroundSize: "24px 24px",
                        }}
                    />

                    <div className="relative mx-auto max-w-[1200px] px-6 py-20 md:py-28">
                        <div className="mx-auto max-w-4xl animate-rise text-center">
                            <h1 className="mx-auto max-w-4xl font-display text-3xl font-bold leading-[1.02] md:text-[2.3rem]">
                                Crowdfunding for Women-Led Ventures
                            </h1>

                            <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-paper/75 md:text-xl">
                                AQi hosts Reg CF offerings from women-owned
                                companies and provides education, community
                                forums, and founder resources. We don&apos;t
                                solicit investments or provide advice; issuers
                                post required disclosures, and investors review
                                them.
                            </p>
                            <p className="mx-auto mt-4 max-w-3xl text-sm text-paper/75 sm:text-base">
                                Have questions about how Regulation Crowdfunding
                                works?{" "}
                                <a
                                    href="/contact-us"
                                    className="font-semibold text-gold underline hover:text-white"
                                >
                                    Contact us
                                </a>{" "}
                                for more information.
                            </p>
                        </div>
                    </div>
                    <div className="h-px bg-gold/40" />
                </div>

                {/* ── Copy Band + CTAs */}
                <div className="px-4 py-8 bg-white sm:py-12 md:py-14 lg:py-16 sm:px-6">
                    <div className="w-full mx-auto text-center space-y-6 border-4 border-[#D4AF37] p-4 sm:p-6 md:p-8 shadow-lg max-w-7xl xl:max-w-8xl">
                        <div className="flex flex-col items-center justify-center gap-6 sm:flex-row sm:gap-8 md:gap-12 lg:gap-16 xl:gap-24">
                            <a
                                href="/faq/public/entrepreneurs"
                                className="bg-navy hover:bg-navy-2 text-white font-semibold rounded-xl px-6 sm:px-8 md:px-10 lg:px-12 py-2 sm:py-3 md:py-4 shadow-lg transition text-xs sm:text-sm md:text-base w-full sm:w-auto text-center"
                            >
                                Entrepreneur&apos;s FAQ
                            </a>
                            <button
                                onClick={handleEntrepreneurClick}
                                className="bg-navy hover:bg-navy-2 text-white font-semibold rounded-xl px-6 sm:px-8 md:px-10 lg:px-12 py-2 sm:py-3 md:py-4 shadow-lg transition text-xs sm:text-sm md:text-base w-full sm:w-auto"
                            >
                                START YOUR APPLICATION
                            </button>

                            <Link
                                to="/startups"
                                className="bg-navy hover:bg-navy-2 text-white font-semibold rounded-xl px-6 sm:px-8 md:px-10 lg:px-12 py-2 sm:py-3 md:py-4 shadow-lg transition text-xs sm:text-sm md:text-base w-full sm:w-auto text-center"
                            >
                                Explore Startups
                            </Link>
                        </div>
                        <div className="mt-4 text-center sm:mt-6">
                            <p className="text-navy text-xs sm:text-sm md:text-base lg:text-lg font-medium flex flex-col sm:flex-row items-center justify-center gap-2 mb-2">
                                <LockClosedIcon className="w-4 h-4 sm:w-5 sm:h-5 text-navy" />
                                <span>
                                    AQi operates under SEC and FINRA regulations
                                    as a registered funding portal.
                                </span>
                            </p>
                            <p className="text-navy text-xs sm:text-sm md:text-base lg:text-lg font-medium mt-1">
                                We offer guidance, not guarantees — your success
                                is driven by your preparation, promotion, and
                                passion.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            <section className="bg-navy text-white font-body px-4 sm:px-6 py-8 sm:py-12 md:py-16">
                <div className="max-w-4xl mx-auto lg:max-w-6xl xl:max-w-7xl 2xl:max-w-8xl">
                    <h2 className="mb-6 text-center text-2xl font-semibold sm:mb-8 sm:text-[2rem] md:mb-10 lg:mb-12 font-display">
                        WHAT SETS AQi APART?
                    </h2>

                    <div className="flex flex-col items-center gap-6 lg:flex-row sm:gap-8 md:gap-10 lg:gap-12">
                        {/* Left - Image */}
                        <div className="w-full lg:w-1/2">
                            <img
                                src="/images/lady2.png"
                                alt="Woman standing confidently in store"
                                className="w-full h-60 sm:h-72 md:h-80 lg:h-96 xl:h-[28rem] object-cover object-center rounded-lg"
                            />
                        </div>

                        {/* Right - Text */}
                        <div className="w-full text-sm leading-relaxed text-white lg:w-1/2 sm:text-base lg:text-lg xl:text-xl">
                            <p className="mb-4">
                                We&apos;re a women‑focused platform, run by
                                people who actually get what it&apos;s like to
                                build a business while navigating bias, burnout,
                                and brilliant ambition. We offer personalized,
                                concierge‑level support to guide you through the
                                process—and connect you with partners if you
                                need extra help along the way. You&apos;ll never
                                be left guessing. We provide specific,
                                transparent feedback on your application so you
                                can move forward with clarity and confidence.
                                And we design every step of the experience with
                                an understanding of the real demands women face
                                in entrepreneurship.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Modal for Entrepreneur Signup */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
                    <div
                        className={
                            `bg-white rounded-lg shadow-xl p-4 sm:p-6 md:p-8 text-center border border-navy relative ` +
                            (modalStep > 3 ? bigModal : smallModal)
                        }
                    >
                        {/* X Close Button for all steps except modalStep 5 (EntrepreneurTermsNConditions handles its own close) */}
                        {modalStep !== 5 && (
                            <button
                                onClick={() => setShowModal(false)}
                                aria-label="Close modal"
                                className="absolute top-2 right-2 text-navy hover:text-[#D4AF37] text-2xl font-bold focus:outline-none z-10 bg-transparent"
                                style={{
                                    lineHeight: "1",
                                    padding: "0 8px",
                                    borderRadius: "50%",
                                }}
                            >
                                &times;
                            </button>
                        )}
                        {modalStep <= 3 && (
                            <img
                                src="AeQuitas-Logo.png"
                                alt="AQI Logo"
                                className="w-32 mx-auto mb-3 sm:w-40 md:w-48 lg:w-52 xl:w-60 sm:mb-4"
                            />
                        )}
                        {modalStep === 1 && (
                            <>
                                <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl text-navy font-medium font-display italic mt-3 sm:mt-4">
                                    Quick question before we dive in…
                                </h2>
                                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-navy font-medium font-body italic mt-3 sm:mt-4">
                                    Is your company at least
                                    <br />
                                    50% woman-owned?
                                </p>
                                <div className="flex flex-col items-center gap-3 mt-4 sm:mt-6">
                                    <button
                                        onClick={() => handleAnswer(true)}
                                        className="bg-navy text-white py-2 sm:py-3 px-4 sm:px-6 rounded-lg font-bold font-body hover:bg-cyan-900 w-full max-w-xs sm:max-w-sm md:max-w-md text-sm sm:text-base"
                                    >
                                        Yes, we&apos;re at least 50% woman-owned
                                    </button>
                                    <button
                                        onClick={() => handleAnswer(false)}
                                        className="border border-navy text-navy py-2 sm:py-3 px-4 sm:px-6 rounded-md font-bold font-body hover:bg-cyan-50 w-full max-w-xs sm:max-w-sm md:max-w-md text-sm sm:text-base"
                                    >
                                        No, we don&apos;t meet that requirement
                                    </button>
                                </div>
                            </>
                        )}

                        {modalStep === 2 && (
                            <>
                                <h2 className="text-xl sm:text-2xl md:text-3xl font-medium font-display italic text-navy mt-2">
                                    Great!
                                </h2>
                                <p className="text-xl sm:text-2xl md:text-3xl font-medium font-body italic text-navy mb-4 sm:mb-6">
                                    Let&apos;s do this!
                                </p>
                                <button
                                    onClick={handleFinalContinue}
                                    className="bg-navy hover:bg-navy text-white font-bold py-2 sm:py-3 px-6 sm:px-8 rounded-xl w-full max-w-xs sm:max-w-sm md:max-w-md text-sm sm:text-base"
                                >
                                    CONTINUE
                                </button>
                            </>
                        )}

                        {modalStep === 3 && (
                            <>
                                <h2 className="text-lg sm:text-xl md:text-2xl font-medium font-display italic text-navy mb-1">
                                    Thank you for your interest in AQi.
                                </h2>
                                <p className="text-sm sm:text-base md:text-lg font-medium font-body italic text-navy leading-relaxed mt-4">
                                    At this time, our platform is dedicated to
                                    <br />
                                    supporting companies that are majority
                                    woman-owned.
                                    <br />
                                    Please check back for future updates or
                                    partner offerings.
                                </p>
                                <div className="flex justify-center mt-4 sm:mt-6">
                                    <button
                                        onClick={handleGoHome}
                                        className="bg-navy hover:bg-navy text-white font-bold font-body py-2 sm:py-3 px-6 sm:px-8 rounded-xl text-sm sm:text-base"
                                    >
                                        HOME
                                    </button>
                                </div>
                            </>
                        )}
                        {modalStep === 4 && (
                            <>
                                <div className="pl-2 text-left sm:pl-4 md:pl-6 lg:pl-8 xl:pl-10">
                                    <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-navy mb-3 sm:mb-4">
                                        Checklist for Getting Started
                                    </h2>

                                    <p className="text-left font-bold text-navy/90 mb-3 sm:mb-4 text-sm sm:text-base md:text-lg">
                                        To ensure your company is eligible to
                                        raise capital on AQi, we required to
                                        confirm that you meet U.S. regulatory
                                        requirements for crowdfunding under
                                        Regulation CF.
                                    </p>
                                    <p className="text-left font-bold text-navy/90 mb-3 sm:mb-4 text-sm sm:text-base md:text-lg">
                                        To continue your application, please
                                        confirm that you can answer yes to all
                                        of the following about your company:
                                    </p>

                                    <ul className="text-left space-y-1 sm:space-y-2 text-navy list-disc list-inside text-xs sm:text-sm md:text-base lg:text-lg">
                                        <li>is at least 50% woman-owned.</li>
                                        <li>
                                            is organized and operating in the
                                            United States and is not
                                            foreign-owned.
                                        </li>
                                        <li>
                                            has not raised more than $5 million
                                            under Reg CF in the past 12 months.
                                        </li>
                                        <li>
                                            If previously conducted a Reg CF
                                            offering, it has filed all required
                                            reports(eg., Form C-AR).
                                        </li>
                                        <li>
                                            Neither the Company nor any of its
                                            officers, directors, general
                                            partners, or 20% beneficial owners
                                            are subject to &quot;bad actor&quot;
                                            disqualifications under SEC rules.
                                        </li>
                                        <li>
                                            is not an investment company or
                                            subject to the Investment Company
                                            Act of 1940.
                                        </li>
                                        <li>
                                            has a current business plan and
                                            intended use of funds.
                                        </li>
                                        <li>
                                            is seeking to raise between $150,000
                                            and $5,000,000 in capital.
                                        </li>
                                    </ul>
                                </div>
                                <button
                                    onClick={handleChecklistYes}
                                    className="mt-4 sm:mt-6 bg-navy hover:bg-navy-2 text-white font-bold py-2 sm:py-3 px-8 sm:px-10 md:px-12 rounded-xl text-sm sm:text-base md:text-lg"
                                >
                                    YES
                                </button>

                                <p className="mt-2 text-xs italic sm:mt-3 sm:text-sm">
                                    If you cannot answer YES to all of the
                                    above, please{" "}
                                    <span
                                        className="text-navy underline cursor-pointer"
                                        onClick={handleGoHome}
                                    >
                                        EXIT
                                    </span>
                                </p>
                            </>
                        )}
                        {modalStep === 5 && (
                            <EntrepreneurTermsNConditions
                                onClose={() => setShowModal(false)}
                                onBack={() => setModalStep(4)}
                                onSuccess={() => {
                                    setShowModal(false);
                                    navigate("/entrepreneur-application");
                                }}
                            />
                        )}
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default EntrepreneurLandingPage;
